import React, { useState } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  HStack,
  IconButton,
  Spacer,
  useColorMode,
  Input,
  InputGroup,
  InputRightElement,
  Icon,
  useToast,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import {
  CloseIcon,
  AddIcon,
  ArrowForwardIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axiosInstance from "../../helpers/axiosInstance"; // Import axios instance
import GridIcon from "../../assets/icons/dot_grid_icon.svg"; // Import the grid icon SVG
import { useCurrency } from "../CurrencyExchange/CurrencyContext"; // Import Currency Context
import Translate from "../../components/LanguageTranslation/Translate";
import ExternalLinkButton from "../ExternalLinkButton";
import AddAccommodationItemModal from "./AddAccommodationItemModal";

const HotelsList = ({
  hotels,
  setHotels,
  onHotelClick,
  tripId,
  location,
  setView,
  handleSuggestionClick,
  isAddAccommodationOpen,
  setIsAddAccommodationOpen,
  city,
}) => {
  // console.log("hotels", hotels);
  const { colorMode } = useColorMode();
  const history = useHistory();
  const toast = useToast();
  const [hoveredItem, setHoveredItem] = useState(null);

  const { convertCurrency, currency } = useCurrency(); // Get the currency context

  // Function to handle drag end event
  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(hotels);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setHotels(items); // Update state with reordered hotels

    // Construct the payload with updated hotel list
    const payload = {
      home_to_go_api_response: items,
      city,
    };

    // Make PUT request to update the order in the backend
    try {
      await axiosInstance.put(`/bucket-list/${tripId}/update/stay`, payload);
    } catch (error) {
      console.error("Error updating hotel order:", error);
    }
  };

  const handleHotelDelete = async (e, hotelId) => {
    e.preventDefault();

    // Extract index from hotelId (format: "hotel-{index}")
    const indexToDelete = parseInt(hotelId.split('-')[1]);
    
    // Remove by index
    const updatedHotels = hotels.filter((_, index) => index !== indexToDelete);
    setHotels(updatedHotels);

    // Construct the payload with updated hotel list
    const payload = {
      home_to_go_api_response: updatedHotels,
      city,
    };

    // Make PUT request to update the list in the backend after deletion
    try {
      await axiosInstance.put(`/bucket-list/${tripId}/update/stay`, payload);
    } catch (error) {
      console.error("Error deleting hotel:", error);
    }
  };

  const handleCloseDetail = () => {
    setView(null);
  };

  return (
    <Box
      bg={colorMode === "dark" ? "gray.800" : "blackAlpha.50"}
      w="100%"
      h="100vh"
      overflowY="hidden"
    >
      <HStack justifyContent="space-between" bg="white">
        <Box
          flex="1"
          display="flex"
          justifyContent="left"
          p={4}
          pt={2}
          pb={2}
          pl={4}
          maxH="8vh"
          borderBottom="1px"
          borderLeft="1px"
          borderColor="blackAlpha.200"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mt={1}
            pb={2}
            color="blackAlpha.700"
          >
            <Translate text="Update Accomodations" />
          </Text>
          <Spacer />
          <Menu>
            <MenuButton
              as={Button}
              bg="#1A202C"
              color="white"
              _hover={{ bg: "#1A202C" }}
              _active={{ bg: "#1A202C" }}
              size={{ base: "xs", md: "sm", lg: "sm" }}
              rightIcon={<ChevronDownIcon />}
              mr={4}
            >
              <Translate text="Update Accomodations" />
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => setIsAddAccommodationOpen(true)}
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Add Bucket List Items" />
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleSuggestionClick(
                    `Could you give me more recommendation for accomodations in ${city}?`,
                    "accommodation_conversation"
                  )
                }
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Get more Recommendations" />
              </MenuItem>
            </MenuList>
          </Menu>
          <IconButton
            icon={<CloseIcon boxSize={2.5} />}
            onClick={handleCloseDetail}
            colorScheme="gray"
            size="sm"
            isRound={true}
            position="relative"
            zIndex="tooltip"
            bg="white"
            boxShadow="md"
          />
        </Box>
      </HStack>
      <Box h={{ base: "91vh", md: "89vh", lg: "89vh" }}>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="hotels">
            {(provided) => (
              <VStack
                spacing={4}
                h="100%"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                bg="white"
                align="stretch"
                p={4}
                m={2}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {hotels.map((hotel, index) => {
                  // Convert hotel price
                  const convertedPrice = hotel.price?.perNight
                    ? convertCurrency(
                        hotel.price.perNight,
                        hotel.price.currency
                      )
                    : null;

                  // Use index as unique identifier for drag and drop
                  const hotelId = `hotel-${index}`;

                  return (
                    <Draggable
                      key={hotelId}
                      draggableId={hotelId}
                      index={index}
                    >
                      {(provided) => (
                        <Box
                          position="relative"
                          bg="white"
                          borderRadius="lg"
                          boxShadow="md"
                          border="1px solid"
                          borderColor="gray.200"
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="space-between"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          onMouseEnter={() => setHoveredItem(index)}
                          onMouseLeave={() => setHoveredItem(null)}
                          // as="a"
                          // href={hotel.link}
                          // target="_blank"
                          // rel="noopener noreferrer"
                          // _hover={{ cursor: "pointer" }}
                        >
                          {/* Numbering circle */}
                          <Box
                            position="absolute"
                            top="-10px"
                            left="-10px"
                            bg="black"
                            color="white"
                            borderRadius="full"
                            width="24px"
                            height="24px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="12px"
                            fontWeight="bold"
                          >
                            {index + 1}
                          </Box>
                          <Box
                            {...provided.dragHandleProps}
                            bg="gray.100"
                            alignContent="center"
                            visibility={
                              hoveredItem === index ? "visible" : "hidden"
                            }
                            h="100%"
                          >
                            <Image
                              src={GridIcon}
                              alt="Drag Handle"
                              boxSize="4"
                              opacity={hoveredItem === index ? 1 : 0.5}
                            />
                          </Box>
                          <VStack ml={1} w="full" p={4} pl={0} h="100%">
                            <HStack align="center" spacing={4} w="100%">
                              <VStack
                                align="flex-start"
                                spacing={1}
                                flex="1"
                                h="100%"
                              >
                                <Text fontSize="18px" fontWeight="bold" mb={0}>
                                  <Translate text={hotel.title} />
                                </Text>
                                <Text
                                  fontSize="sm"
                                  fontWeight="bold"
                                  color="gray.500"
                                  // ml={1}
                                  mb={0}
                                >
                                  <Translate
                                    text={hotel.type.replace("_", " ")}
                                  />
                                </Text>
                                <Text fontSize="14px" color="gray.600" mb={0}>
                                  {currency}{" "}
                                  {convertedPrice
                                    ? convertedPrice
                                    : hotel.price?.perNight}{" "}
                                  per night -{" "}
                                  {hotel.rating?.value ? (
                                    `${hotel.rating.value} ★`
                                  ) : (
                                    <Translate text="No Rating" />
                                  )}{" "}
                                  (
                                  {hotel.reviews || (
                                    <Translate text="No reviews available" />
                                  )}
                                  )
                                </Text>
                              </VStack>
                              {hotel.images && hotel.images[0]?.medium && (
                                <Image
                                  src={
                                    `https:${hotel.images[0]?.medium}` ||
                                    "https://via.placeholder.com/120"
                                  }
                                  alt={hotel.title}
                                  boxSize="120px"
                                  borderRadius="lg"
                                  objectFit="cover"
                                />
                              )}
                            </HStack>
                            <HStack spacing={1} w="100%">
                              {hotel.link && (
                                <ExternalLinkButton
                                  as="a"
                                  href={hotel.link}
                                  rel="noopener noreferrer"
                                  size="sm"
                                  bg="#286328"
                                  _hover="none"
                                  _active="none"
                                  textColor="white"
                                  mt={2}
                                >
                                  <Translate text="Open" />
                                </ExternalLinkButton>
                              )}
                              <Spacer />
                              <IconButton
                                icon={<FaTrash />}
                                aria-label="Delete hotel"
                                borderRadius="full"
                                size="xs"
                                visibility={
                                  hoveredItem === index ? "visible" : "hidden"
                                }
                                onClick={(e) => handleHotelDelete(e, hotelId)}
                              />
                            </HStack>
                          </VStack>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </VStack>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <AddAccommodationItemModal
        isOpen={isAddAccommodationOpen}
        onClose={() => setIsAddAccommodationOpen(false)}
        tripId={tripId}
        // addTripItem={(newItem) => {
        //   setHotels((prev) => [...prev, newItem]);
        // }}
        city={city}
        hotels={hotels}
        setHotels={setHotels}
      />
    </Box>
  );
};

export default HotelsList;
